/**
 * Step building blocks shared by every Punch Lists tour
 * (``list.ts``, ``detail.ts``, ``combined.ts``).
 *
 * The combined tour reuses the list-side and detail-side step bodies
 * verbatim — only the welcome step and the "table → detail" transition
 * step differ between flavors.
 */
import type { TourStep } from '../types';
import { clickPreferReact, has, waitForCondition } from '../utils/dom';

// ---------------------------------------------------------------------------
// Selectors / predicates
// ---------------------------------------------------------------------------

/** True once the punch-list detail page is mounted in the DOM. */
export const hasDetailMounted = (): boolean =>
  has('[data-tour-id="punch-list-detail-header-actions"]');

// ---------------------------------------------------------------------------
// List-side steps
// ---------------------------------------------------------------------------

export const newItemStep: TourStep = {
  id: 'new-item',
  target: '[data-tour-id="punch-lists-new-item"]',
  placement: 'left',
  title: 'Add a new item',
  body: 'Click here to log a punch list item from scratch.',
};

export const statusChartStep: TourStep = {
  id: 'status-chart',
  target: '[data-tour-id="punch-lists-summary-toggle"]',
  placement: 'right',
  title: 'Summary on demand',
  body:
    'Open the summary when you want status, priority, and progress charts. ' +
    'On phones, it stays out of the way by default.',
};

export const filtersStep: TourStep = {
  id: 'filters',
  target: '[data-tour-id="punch-lists-filters"]',
  placement: 'bottom',
  spotlightPadding: 4,
  title: 'Find what you need',
  body: 'Search by keyword or filter by status and priority to narrow the list down.',
};

/** "Open an item" — list-only flavor (no row click, just description). */
export const listTableStep: TourStep = {
  id: 'table',
  target: '[data-tour-id="punch-lists-table"]',
  placement: 'top',
  spotlightPadding: 4,
  title: 'Open an item',
  body: 'Each row is one item. Click any row to open the full details.',
};

/**
 * "Open an item" — combined flavor: clicking Next opens the first row
 * and waits for the detail page to mount before letting the tour
 * advance into the detail steps.
 */
export const combinedTableStep: TourStep = {
  id: 'open-an-item',
  target: '[data-tour-id="punch-lists-table"]',
  placement: 'top',
  spotlightPadding: 4,
  title: 'Open an item',
  body:
    'Click any row to open it. Press Next and we\u2019ll open the first ' +
    'one for you.',
  onBeforeNext: async () => {
    // ``handleRowClick`` on TableRow calls ``navigate(item.id)`` which
    // takes us to the detail page. Wait for the detail page's first
    // ``data-tour-id`` to mount so the next step's spotlight has a target.
    clickPreferReact('[data-tour-id="punch-lists-table"] tbody tr');
    await waitForCondition(hasDetailMounted, 4000, 'row click → detail page');
  },
  // User clicked a row themselves → detail page mounted → advance.
  autoAdvanceWhen: () => hasDetailMounted(),
};

// ---------------------------------------------------------------------------
// Detail-side steps
// ---------------------------------------------------------------------------

export const headerActionsStep: TourStep = {
  id: 'header-actions',
  target: '[data-tour-id="punch-list-detail-header-actions"]',
  placement: 'bottom',
  spotlightPadding: 4,
  title: 'Status & actions',
  body:
    'Update the item\u2019s status, remove it, or jump straight to its ' +
    '360\u00B0 hotspot if it has one.',
};

export const metadataStep: TourStep = {
  id: 'metadata',
  target: '[data-tour-id="punch-list-detail-metadata"]',
  placement: 'bottom',
  spotlightPadding: 4,
  title: 'Key fields',
  body:
    'A quick summary of the most important fields — priority, due date, ' +
    'assignee, type, location, and trade.',
};

export const descriptionStep: TourStep = {
  id: 'description',
  target: '[data-tour-id="punch-list-detail-description"]',
  placement: 'right',
  spotlightPadding: 4,
  title: 'Description',
  body: 'The full write-up of what needs to be done.',
};

export const attachmentsStep: TourStep = {
  id: 'attachments',
  target: '[data-tour-id="punch-list-detail-attachments"]',
  placement: 'right',
  spotlightPadding: 4,
  title: 'Attachments',
  body:
    'Upload photos, drawings, or any supporting files. Image attachments ' +
    'show up in a gallery; everything else lives in a downloads list.',
};

export const peopleStep: TourStep = {
  id: 'people',
  target: '[data-tour-id="punch-list-detail-people"]',
  placement: 'left',
  spotlightPadding: 4,
  title: 'People',
  body:
    'Who\u2019s involved — punch manager, assignee, final approver, plus ' +
    'anyone on the distribution list.',
};
